import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type {
  ProcessDefinitionView,
  ProcessReadinessView,
  ProcessStatsView,
  ProcessVersionsView,
} from "@/types";

// #282: Prozess-Registry (Betreiber-Sicht). Verwaltung admin bzw. process-owner (Bundle).

export function useProcesses(): UseQueryResult<ProcessDefinitionView[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("processes"),
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ProcessDefinitionView[]>("/processes");
    },
    enabled: !!session,
  });
}

export function useProcess(id: string | undefined): UseQueryResult<ProcessDefinitionView> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("processes", id) : ["resources", "processes", "_invalid"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ProcessDefinitionView>(`/processes/${id}`);
    },
    enabled: !!session && !!id,
  });
}

// Nachtrag 2026-08-22: Versions-Sicht — was sagt die process.json, welche Versionen
// existieren schon (Tags), was ist aktiv. Speist den Freigeben-&-Aktivieren-Flow.
export function useProcessVersions(id: string | undefined): UseQueryResult<ProcessVersionsView> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? [...queryKeys.detail("processes", id), "versions"] : ["resources", "processes", "_invalid", "versions"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ProcessVersionsView>(`/processes/${id}/versions`);
    },
    enabled: !!session && !!id,
    // Bewusst initial (die Sektion IST diese Sicht) — aber gedrosselt: der Endpoint liest
    // live aus Gitea (LOW-9); Aktionen invalidieren ohnehin gezielt.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * #303 (UC-2): Lauf-Kennzahlen der Vorlage (Aggregat, KEINE Feldinhalte).
 *
 * ⚠ Der Master antwortet hier **404 statt 403**, wenn jemand die Zahlen nicht sehen darf
 * (sie sind eine Aussage über FREMDE Vorgänge, und `processes:read` trägt jede
 * process-owner-Rolle). Die Karte darf daraus also KEIN „gibt es nicht" machen — sie
 * blendet sich still aus. Deshalb `retry: false`.
 */
export function useProcessStats(id: string | undefined): UseQueryResult<ProcessStatsView> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id
      ? queryKeys.subResource("processes", id, "stats")
      : ["resources", "processes", "_invalid", "stats"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ProcessStatsView>(`/processes/${id}/stats`);
    },
    enabled: !!session && !!id,
    retry: false,
  });
}

// Bereitschaft — bewusst NUR auf Klick (enabled-Flag), nicht bei jedem Seitenaufruf:
// der Check liest live aus Gitea.
export function useProcessReadiness(
  id: string | undefined,
  version: string | undefined,
  enabled: boolean,
): UseQueryResult<ProcessReadinessView> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id
      ? [...queryKeys.subResource("processes", id, "readiness"), version ?? "live"]
      : ["resources", "processes", "_invalid", "readiness"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      const q = version ? `?version=${encodeURIComponent(version)}` : "";
      return client.get<ProcessReadinessView>(`/processes/${id}/readiness${q}`);
    },
    enabled: !!session && !!id && enabled,
    retry: false,
  });
}
